import { useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/ui/icon.js';
import { useDisplayMoney, useFormatMoney } from '@/components/money/MoneyProvider.js';
import { formatMoney } from '@/lib/money/index.js';
import { fetchRecentFeed } from '@/features/originals/feed/api.js';
import { ORIGINALS_ART } from '@/features/originals/catalog.js';
import { originalName } from '@/features/originals/names.js';
import { useAuthStore } from '@/stores/auth.js';

const FALLBACK_ART = { icon: 'star' as IconName, grad: 'var(--art-1)' };

const ROWS_VISIBLE = 12;
// Fetch extra rows below the fold; the slide-down reveals/clips them so there's never an
// empty gap at the bottom of the viewport while the list glides. The buffer size also caps
// how far a single glide travels, which keeps a burst of bets flowing smoothly (see effect).
const ROW_BUFFER = 6;
const FEED_LIMIT = ROWS_VISIBLE + ROW_BUFFER;

/**
 * Duel-style "Live Bets" table. Backed by the real Originals feed
 * (/originals/feed/recent) — there is no global casino-wide feed yet, so this
 * shows live in-house game activity. All / Mine toggle, polls every 4s.
 */
export function CasinoLiveBets() {
  const { t } = useTranslation('casino');
  const money = useDisplayMoney();
  const formatDisplay = useFormatMoney();
  // Same order as the balance pill: the wallet's own currency leads and the
  // storefront conversion trails it, only while a rate is actually in force.
  const formatHeld = (m: bigint) => formatMoney(m, money.ledgerCurrency);
  const converted = money.rate !== null && money.currency !== money.ledgerCurrency;
  const [mine, setMine] = useState(false);
  // The feed is read per member's agent and names other players, so it is not
  // served to a visitor who has not signed in: nothing is polled and nothing shown.
  const isGuest = !useAuthStore((s) => s.accessToken);

  const feed = useQuery({
    queryKey: ['originals', 'feed', mine],
    queryFn: () => fetchRecentFeed({ limit: FEED_LIMIT, mine }),
    refetchInterval: 4000,
    staleTime: 2000,
    enabled: !isGuest,
  });
  const rows = feed.data?.rows ?? [];

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const prevIdsRef = useRef<string[]>([]);
  const prevMineRef = useRef(mine);

  // BC.Game/Stake-style conveyor: when new rounds are prepended at the top, the whole
  // list glides down by their combined height in a single motion (FLIP on the list
  // container), so every existing row slides down together — not a per-row flash. The
  // viewport is a fixed height with overflow:hidden, so the document height never changes
  // and the PAGE scrollbar doesn't jump. New rows slide in from above; the surplus rows
  // below the fold slide out the bottom (clipped).
  useLayoutEffect(() => {
    const list = listRef.current;
    const viewport = viewportRef.current;
    if (!list || !viewport) return;

    const ids = rows.map((r) => r.roundId);
    const prevIds = prevIdsRef.current;
    const tabChanged = prevMineRef.current !== mine;
    prevIdsRef.current = ids;
    prevMineRef.current = mine;

    const rowH = (list.firstElementChild as HTMLElement | null)?.offsetHeight ?? 0;
    if (rowH > 0) {
      viewport.style.height = `${rowH * Math.min(ids.length, ROWS_VISIBLE)}px`;
    }

    // Skip on first paint, an All/Mine tab switch, or reduced-motion — but NOT on a full
    // data turnover, which is exactly what rapid betting produces (more new rounds than the
    // window holds between two 4s polls) and must still animate.
    if (prevIds.length === 0 || rowH === 0 || tabChanged) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const prev = new Set(prevIds);
    let prepended = 0;
    for (const id of ids) {
      if (prev.has(id)) break;
      prepended += 1;
    }
    if (prepended === 0) return;

    // Cap the glide distance to the buffer so a burst (or full turnover) reads as a smooth
    // downward flow instead of freezing or revealing an empty gap below the fold.
    const glideRows = Math.min(prepended, ROW_BUFFER);
    const shift = rowH * glideRows;
    list.style.transition = 'none';
    list.style.transform = `translateY(${-shift}px)`;
    void list.offsetHeight; // commit the shifted-up start position before transitioning back
    requestAnimationFrame(() => {
      list.style.transition = 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)';
      list.style.transform = 'translateY(0)';
    });
  }, [rows, mine]);

  const tab = (active: boolean) =>
    [
      'h-8 px-3.5 rounded-lg text-[12px] font-bold transition-all',
      active ? 'bg-[var(--accent)] text-[var(--accent-on)] shadow-[0_4px_14px_var(--accent-glow)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
    ].join(' ');

  if (isGuest) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 className="text-[17px] md:text-xl font-bold tracking-tight">{t('liveBets.title')}</h2>
        <div className="ml-auto flex gap-1 p-1 rounded-xl bg-[var(--bg-sunken)]">
          <button type="button" className={tab(!mine)} onClick={() => setMine(false)}>{t('liveBets.all')}</button>
          <button type="button" className={tab(mine)} onClick={() => setMine(true)}>{t('liveBets.mine')}</button>
        </div>
      </div>

      <div
        className="overflow-hidden rounded-2xl border border-transparent shadow-[0_18px_44px_-16px_rgba(0,0,0,0.65)]"
        style={{ background: 'color-mix(in srgb, var(--accent) 7%, var(--bg-raised))' }}
      >
        {/* header */}
        <div className="grid grid-cols-[1.4fr_1.4fr_1fr_0.8fr_1fr] gap-3 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)] border-b border-white/[0.06] bg-black/15">
          <span>{t('liveBets.game')}</span>
          <span>{t('liveBets.player')}</span>
          <span className="text-right">{t('liveBets.wager')}</span>
          <span className="text-right">{t('liveBets.multi')}</span>
          <span className="text-right">{t('liveBets.payout')}</span>
        </div>

        {rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-[13px] text-[var(--text-muted)]">{t('liveBets.empty')}</div>
        ) : (
          <div ref={viewportRef} className="overflow-hidden">
            <div ref={listRef} className="will-change-transform">
              {rows.map((r) => {
                const art = ORIGINALS_ART[r.gameCode] ?? FALLBACK_ART;
                const name = r.gameName ?? originalName(r.gameCode, t);
                return (
                  <div
                    key={r.roundId}
                    className="grid grid-cols-[1.4fr_1.4fr_1fr_0.8fr_1fr] items-center gap-3 px-4 py-3 text-[12.5px] border-b border-white/[0.05] transition-colors hover:bg-[var(--accent)]/[0.07]"
                  >
                    <span className="flex items-center gap-2.5 text-[var(--text-primary)] truncate">
                      {r.thumbnailUrl ? (
                        <img
                          src={r.thumbnailUrl}
                          alt=""
                          loading="lazy"
                          className="h-7 w-7 shrink-0 rounded-lg object-cover shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
                        />
                      ) : (
                        <span
                          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
                          style={{ background: art.grad }}
                          aria-hidden
                        >
                          <Icon name={art.icon} size={14} className="text-white" />
                        </span>
                      )}
                      <span className="truncate font-medium">{name}</span>
                    </span>
                    <span className="font-medium text-[var(--text-secondary)] truncate">{r.player}</span>
                    <span className="text-right bf-tnum text-[var(--text-secondary)]">
                      <span className="block whitespace-nowrap">{formatHeld(BigInt(r.bet))}</span>
                      {converted && (
                        <span className="block whitespace-nowrap text-[11px] leading-tight font-normal text-[var(--text-muted)]">{formatDisplay(BigInt(r.bet))}</span>
                      )}
                    </span>
                    <span className="text-right bf-tnum text-[var(--text-secondary)]">{r.multiplier}×</span>
                    <span className={['text-right bf-tnum font-bold', r.win ? 'text-[var(--success)]' : 'text-[var(--text-muted)]'].join(' ')}>
                      {r.win ? (
                        <>
                          <span className="block whitespace-nowrap">{formatHeld(BigInt(r.payout))}</span>
                          {converted && (
                            <span className="block whitespace-nowrap text-[11px] leading-tight font-normal text-[var(--text-muted)]">{formatDisplay(BigInt(r.payout))}</span>
                          )}
                        </>
                      ) : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
